<template>
  <div class="min-h-screen p-4 sm:p-6 lg:p-8 font-sans">
    <div
      class="max-w-4xl mx-auto bg-white rounded-3xl shadow-1xl border border-gray-300 relative overflow-hidden"
    >
      <!-- Loading Overlay -->
      <div
        v-if="isLoading"
        class="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <div class="text-center">
          <div class="relative">
            <!-- Animated Spinner -->
            <div
              class="w-12 h-12 sm:w-16 sm:h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"
            ></div>
            <!-- Pulsing Dots -->
            <div class="flex justify-center space-x-1 mb-4">
              <div class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
              <div
                class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"
                style="animation-delay: 0.1s"
              ></div>
              <div
                class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"
                style="animation-delay: 0.2s"
              ></div>
            </div>
          </div>
          <h3 class="text-base sm:text-lg font-semibold text-gray-700 mb-2">
            {{ loadingMessage }}
          </h3>
          <p class="text-xs sm:text-sm text-gray-500">
            Please wait while we load your data...
          </p>
        </div>
      </div>

      <div class="p-6 sm:p-8 lg:p-10">
        <h1
          class="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-8 border-b-2 border-blue-100 pb-4 text-center sm:text-left"
        >
          My Profile
        </h1>

        <div v-if="currentUser">
          <!-- Profile Header & Photo Upload -->
          <section
            class="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 mb-10 pb-8 border-b border-gray-100"
          >
            <div class="relative group flex-shrink-0">
              <img
                :src="
                  photoPreview ||
                  displayPhotoUrl ||
                  '/placeholder.svg?height=128&width=128'
                "
                :alt="currentUser.name || 'Profile Photo'"
                class="w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover border-4 border-blue-300 shadow-lg transition-transform duration-300 group-hover:scale-105"
              />
              <label
                for="profile-photo-upload"
                class="absolute bottom-0 right-0 bg-blue-600 text-white p-3 rounded-full cursor-pointer hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform group-hover:scale-110"
                title="Change profile photo"
              >
                <Camera class="w-5 h-5 sm:w-6 sm:h-6" />
                <input
                  id="profile-photo-upload"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handlePhotoUpload"
                  :disabled="isLoading"
                />
              </label>
            </div>

            <div class="text-center sm:text-left flex-1 min-w-0">
              <h2
                class="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight"
              >
                {{ currentUser.name || "N/A" }}
              </h2>
              <p class="text-lg text-gray-600 mt-2">
                Role:
                <span class="font-semibold text-blue-700">{{
                  capitalize(currentUser.role) || "N/A"
                }}</span>
              </p>
              <!-- <p class="text-sm text-gray-500 mt-2">
                School ID:
                <span
                  class="font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded-md text-xs"
                  >{{ currentUser.schoolId || "N/A" }}</span
                >
              </p> -->

              <button
                v-if="photoPreview && photoPreview !== displayPhotoUrl"
                @click="updateProfilePhoto"
                :disabled="isSaving"
                class="mt-5 inline-flex items-center px-5 py-2.5 border border-transparent text-base font-medium rounded-lg shadow-md text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
              >
                <UploadCloud class="w-5 h-5 mr-2" />
                {{ isSaving ? "Uploading..." : "Save New Photo" }}
              </button>
            </div>
          </section>

          <!-- Common Details -->
          <section
            class="mb-10 space-y-6"
            v-if="currentUser.role !== 'admin'"
          >
            <h3
              class="text-2xl font-bold text-gray-800 border-b border-gray-200 pb-3 mb-4 flex items-center"
            >
              <Info class="w-6 h-6 mr-3 text-blue-500" /> General Information
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Email:</span>
                  {{ currentUser.email || "N/A" }}
                </p>
              </div>
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Phone:</span>
                  {{ currentUser.phone || "N/A" }}
                </p>
              </div>
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Status:</span>
                  <span
                    :class="
                      currentUser.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    "
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ml-2"
                  >
                    {{ capitalize(currentUser.status) || "N/A" }}
                  </span>
                </p>
              </div>
              <div
                v-if="
                  currentUser.role !== 'teacher' && currentUser.role !== 'admin'
                "
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Address:</span>
                  {{ currentUser.address || "N/A" }}
                </p>
              </div>
              <div
                v-if="currentUser.role !== 'admin'"
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm col-span-full"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Remarks:</span>
                  {{ currentUser.remark || "N/A" }}
                </p>
              </div>
            </div>
          </section>

          <!-- Student Specific Details -->
          <section
            v-if="currentUser.role === 'student'"
            class="mb-10 space-y-6"
          >
            <h3
              class="text-2xl font-bold text-gray-800 border-b border-gray-200 pb-3 mb-4 flex items-center"
            >
              <GraduationCap class="w-6 h-6 mr-3 text-blue-500" /> Student
              Details
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Class:</span>
                  {{ currentUser.class || "N/A" }}
                </p>
              </div>
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Roll Number:</span>
                  {{ currentUser.rollNumber || "N/A" }}
                </p>
              </div>
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900"
                    >Enrollment Date:</span
                  >
                  {{ formatDate(currentUser.enrollmentDate) || "N/A" }}
                </p>
              </div>
            </div>

            <div class="mt-8 space-y-4">
              <h4 class="text-xl font-semibold text-gray-800 flex items-center">
                <FileText class="w-5 h-5 mr-2 text-blue-500" /> Documents:
              </h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div
                  v-if="currentUser.birthCertificate"
                  class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
                >
                  <span class="font-semibold text-gray-900 text-base"
                    >Birth Certificate:</span
                  >
                  <a
                    :href="currentUser.birthCertificate"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:underline flex items-center text-sm mt-2 transition-colors duration-200"
                  >
                    <Paperclip class="w-4 h-4 mr-2" /> View Document
                  </a>
                </div>
                <div
                  v-if="currentUser.transferCertificate"
                  class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
                >
                  <span class="font-semibold text-gray-900 text-base"
                    >Transfer Certificate:</span
                  >
                  <a
                    :href="currentUser.transferCertificate"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:underline flex items-center text-sm mt-2 transition-colors duration-200"
                  >
                    <Paperclip class="w-4 h-4 mr-2" /> View Document
                  </a>
                </div>
                <div
                  v-if="currentUser.marksheet"
                  class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
                >
                  <span class="font-semibold text-gray-900 text-base"
                    >Marksheet:</span
                  >
                  <a
                    :href="currentUser.marksheet"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:underline flex items-center text-sm mt-2 transition-colors duration-200"
                  >
                    <Paperclip class="w-4 h-4 mr-2" /> View Document
                  </a>
                </div>
              </div>
            </div>
          </section>

          <!-- Teacher Specific Details -->
          <section
            v-else-if="currentUser.role === 'teacher'"
            class="mb-10 space-y-6"
          >
            <h3
              class="text-2xl font-bold text-gray-800 border-b border-gray-200 pb-3 mb-4 flex items-center"
            >
              <Briefcase class="w-6 h-6 mr-3 text-blue-500" /> Teacher Details
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900"
                    >Date of Birth:</span
                  >
                  {{ formatDate(currentUser.dob) || "N/A" }}
                </p>
              </div>
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Subject:</span>
                  {{ currentUser.subject || "N/A" }}
                </p>
              </div>
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900"
                    >Aadhaar Number:</span
                  >
                  {{ currentUser.aadhaarNumber || "N/A" }}
                </p>
              </div>
              <div
                v-if="currentUser.aadhaarCard"
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <span class="font-semibold text-gray-900 text-base"
                  >Aadhaar Card:</span
                >
                <a
                  :href="currentUser.aadhaarCard"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-600 hover:underline flex items-center text-sm mt-2 transition-colors duration-200"
                >
                  <Paperclip class="w-4 h-4 mr-2" /> View Document
                </a>
              </div>
            </div>

            <div
              v-if="
                currentUser.qualifications &&
                currentUser.qualifications.length > 0
              "
              class="mt-8 space-y-4"
            >
              <h4 class="text-xl font-semibold text-gray-800 flex items-center">
                <Award class="w-5 h-5 mr-2 text-blue-500" /> Qualifications:
              </h4>
              <ul class="list-none pl-0 space-y-3">
                <li
                  v-for="(q, index) in currentUser.qualifications"
                  :key="index"
                  class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
                >
                  <p class="text-gray-700 text-base">
                    <span class="font-semibold text-gray-900">{{
                      q.type || "N/A"
                    }}</span>
                    from
                    <span class="font-medium">{{
                      q.institution || "N/A"
                    }}</span>
                    ({{ q.year || "N/A" }})
                  </p>
                  <div v-if="q.fileUrl">
                    <a
                      :href="q.fileUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-blue-600 hover:underline flex items-center text-sm mt-2 transition-colors duration-200"
                    >
                      <Paperclip class="w-4 h-4 mr-2" /> View Document
                    </a>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          <!-- Admin Specific Details -->
          <section
            v-else-if="currentUser.role === 'admin'"
            class="mb-10 space-y-6"
          >
            <h3
              class="text-2xl font-bold text-gray-800 border-b border-gray-200 pb-3 mb-4 flex items-center"
            >
              <ShieldCheck class="w-6 h-6 mr-3 text-blue-500" /> Admin Details
            </h3>

            <!-- Admin Missing Contact helper -->
            <section
              v-if="isVisible"
              class="space-y-4 bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm"
            >
              <h4 class="text-lg font-semibold text-blue-900 flex items-center">
                <Info class="w-5 h-5 mr-2 text-blue-600" />
                Complete Admin Contact Info
              </h4>
              <p class="text-sm text-blue-800">
                Your profile is missing contact information. Please add at least
                one: email or phone.
              </p>

              <form
                @submit.prevent="saveContact"
                class="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                <div class="col-span-1">
                  <label
                    for="admin-email"
                    class="block text-sm font-medium text-blue-900 mb-2"
                    >Email</label
                  >
                  <input
                    id="admin-email"
                    v-model.trim="form.email"
                    :disabled="!!currentUser.email || isSaving"
                    placeholder="admin@example.com"
                    class="block w-full border border-blue-200 rounded-md shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                    type="email"
                  />
                  <p
                    v-if="errors.email"
                    class="mt-1 text-sm text-red-600"
                  >
                    {{ errors.email }}
                  </p>
                  <p
                    v-else-if="currentUser.email"
                    class="mt-1 text-xs text-gray-500"
                  >
                    Already set
                  </p>
                </div>

                <div class="col-span-1">
                  <label
                    for="admin-phone"
                    class="block text-sm font-medium text-blue-900 mb-2"
                    >Phone</label
                  >
                  <input
                    id="admin-phone"
                    v-model.trim="form.phone"
                    :disabled="!!currentUser.phone || isSaving"
                    :placeholder="currentUser.phone || '9876543210'"
                    class="block w-full border border-blue-200 rounded-md shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                    type="tel"
                  />
                  <p
                    v-if="errors.phone"
                    class="mt-1 text-sm text-red-600"
                  >
                    {{ errors.phone }}
                  </p>
                  <p
                    v-else-if="currentUser.phone"
                    class="mt-1 text-xs text-gray-500"
                  >
                    Already set
                  </p>
                </div>

                <div class="col-span-full flex items-center justify-end gap-3">
                  <p
                    v-if="status.message"
                    :class="statusClass"
                    class="text-sm"
                  >
                    {{ status.message }}
                  </p>
                  <button
                    type="submit"
                    :disabled="isSaving"
                    class="inline-flex items-center px-5 py-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow disabled:opacity-60 disabled:cursor-not-allowed transition"
                  >
                    <svg
                      v-if="isSaving"
                      class="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>{{
                      isSaving ? "Saving..." : "Save Contact Info"
                    }}</span>
                  </button>
                </div>
              </form>
            </section>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Email:</span>
                  {{ currentUser.email || "N/A" }}
                </p>
              </div>
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Phone:</span>
                  {{ currentUser.phone || "N/A" }}
                </p>
              </div>
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">School ID:</span>
                  {{ currentUser.schoolId || "N/A" }}
                </p>
              </div>
              <div
                class="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm"
              >
                <p class="text-gray-700 text-base">
                  <span class="font-semibold text-gray-900">Role:</span>
                  {{ capitalize(currentUser.role) || "N/A" }}
                </p>
              </div>
            </div>
          </section>

          <!-- Document Change Warning - Hidden for Admin -->
          <div
            v-if="currentUser.role !== 'admin'"
            class="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-5 rounded-lg mb-10 shadow-md"
            role="alert"
          >
            <div class="flex items-center">
              <AlertTriangle
                class="w-6 h-6 mr-3 flex-shrink-0 text-yellow-500"
              />
              <p class="text-sm sm:text-base font-medium">
                To change any document (e.g., Birth Certificate, Aadhaar Card,
                Qualification documents), please contact the Admin or Principal.
              </p>
            </div>
          </div>

          <!-- Change Password Section -->
          <section class="space-y-6">
            <h3
              class="text-2xl font-bold text-gray-800 border-b border-gray-200 pb-3 mb-4 flex items-center"
            >
              <Lock class="w-6 h-6 mr-3 text-blue-500" />
              <span>{{
                isInitialPasswordSetup ? "Set Password" : "Change Password"
              }}</span>
            </h3>

            <!-- Optional helper for first-time setup -->
            <p
              v-if="isInitialPasswordSetup"
              class="text-sm text-gray-600"
            >
              It looks like you don't have a password yet. Please set a new
              password below.
            </p>

            <!-- OTP Block (only for initial setup) -->
            <div
              v-if="isInitialPasswordSetup"
              class="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3"
            >
              <div class="flex flex-col sm:flex-row sm:items-end gap-3">
                <div class="flex-1">
                  <label
                    for="otp"
                    class="block text-sm font-medium text-blue-900 mb-2"
                  >
                    One-Time Password (OTP)
                  </label>
                  <input
                    id="otp"
                    v-model.trim="otp"
                    maxlength="6"
                    inputmode="numeric"
                    placeholder="6-digit code"
                    class="block w-full border border-blue-200 rounded-md shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    :disabled="isOtpSending || isSaving || !canSendOtp"
                  />
                  <p
                    v-if="!canSendOtp"
                    class="mt-1 text-xs text-blue-800"
                  >
                    Add a phone number first to receive OTP.
                  </p>
                </div>

                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    @click="sendOtp"
                    :disabled="!canSendOtp || isOtpSending || resendSeconds > 0"
                    class="inline-flex items-center px-4 py-2.5 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed shadow"
                  >
                    <svg
                      v-if="isOtpSending"
                      class="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>
                      {{
                        otpSent
                          ? resendSeconds > 0
                            ? `Resend in ${resendSeconds}s`
                            : "Resend OTP"
                          : "Send OTP"
                      }}
                    </span>
                  </button>
                </div>
              </div>

              <p
                v-if="otpSent"
                class="text-xs text-blue-700"
              >
                We sent an OTP to your registered phone number. The code expires
                in 5 minutes.
              </p>
            </div>

            <form
              @submit.prevent="changePassword"
              class="space-y-5"
            >
              <!-- Current Password (only when a password already exists) -->
              <div
                class="relative"
                v-if="!isInitialPasswordSetup"
              >
                <label
                  for="current-password"
                  class="block text-sm font-medium text-gray-700 mb-2"
                >
                  Current Password
                </label>
                <input
                  id="current-password"
                  :type="currentPasswordType"
                  v-model="passwordForm.currentPassword"
                  :disabled="isLoading"
                  class="block w-full border border-gray-300 rounded-lg shadow-sm py-2.5 px-4 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 text-gray-900 placeholder-gray-400 disabled:opacity-50 disabled:bg-gray-100"
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('current')"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center pt-8 text-gray-500 hover:text-gray-700 focus:outline-none"
                  :disabled="isLoading"
                >
                  <Eye
                    v-if="currentPasswordType === 'password'"
                    class="h-5 w-5"
                  />
                  <EyeOff
                    v-else
                    class="h-5 w-5"
                  />
                </button>
              </div>

              <!-- New Password -->
              <div class="relative">
                <label
                  for="new-password"
                  class="block text-sm font-medium text-gray-700 mb-2"
                >
                  New Password
                </label>
                <input
                  id="new-password"
                  :type="newPasswordType"
                  v-model="passwordForm.newPassword"
                  :disabled="isLoading"
                  class="block w-full border border-gray-300 rounded-lg shadow-sm py-2.5 px-4 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 text-gray-900 placeholder-gray-400 disabled:opacity-50 disabled:bg-gray-100"
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('new')"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center pt-8 text-gray-500 hover:text-gray-700 focus:outline-none"
                  :disabled="isLoading"
                >
                  <Eye
                    v-if="newPasswordType === 'password'"
                    class="h-5 w-5"
                  />
                  <EyeOff
                    v-else
                    class="h-5 w-5"
                  />
                </button>
              </div>

              <!-- Confirm Password -->
              <div class="relative">
                <label
                  for="confirm-password"
                  class="block text-sm font-medium text-gray-700 mb-2"
                >
                  Confirm New Password
                </label>
                <input
                  id="confirm-password"
                  :type="confirmPasswordType"
                  v-model="passwordForm.confirmPassword"
                  :disabled="isLoading"
                  class="block w-full border border-gray-300 rounded-lg shadow-sm py-2.5 px-4 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 text-gray-900 placeholder-gray-400 disabled:opacity-50 disabled:bg-gray-100"
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('confirm')"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center pt-8 text-gray-500 hover:text-gray-700 focus:outline-none"
                  :disabled="isLoading"
                >
                  <Eye
                    v-if="confirmPasswordType === 'password'"
                    class="h-5 w-5"
                  />
                  <EyeOff
                    v-else
                    class="h-5 w-5"
                  />
                </button>
              </div>

              <div class="flex justify-end">
                <button
                  type="submit"
                  :disabled="
                    isSaving ||
                    (isInitialPasswordSetup && (!otp || otp.length !== 6))
                  "
                  class="inline-flex justify-center py-3 px-6 border border-transparent shadow-md text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
                >
                  <span
                    v-if="isSaving"
                    class="flex items-center"
                  >
                    <svg
                      class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </span>
                  <span v-else>
                    {{
                      isInitialPasswordSetup
                        ? "Set Password"
                        : "Change Password"
                    }}
                  </span>
                </button>
              </div>
            </form>

            <!-- Toast -->
            <transition name="fade">
              <div
                v-if="toast.isVisible"
                :class="toastClasses"
                class="fixed bottom-6 right-6 p-4 rounded-lg shadow-xl text-white text-base font-medium z-50 flex items-center space-x-2 min-w-[250px]"
              >
                <Info
                  v-if="toast.type === 'info'"
                  class="w-5 h-5"
                />
                <CheckCircle
                  v-if="toast.type === 'success'"
                  class="w-5 h-5"
                />
                <XCircle
                  v-if="toast.type === 'error'"
                  class="w-5 h-5"
                />
                <span>{{ toast.message }}</span>
              </div>
            </transition>
          </section>
        </div>

        <div
          v-else
          class="text-center py-16 text-gray-500"
        >
          <p class="text-xl font-medium">Loading profile data...</p>
          <p class="text-base mt-3">
            Please ensure you are logged in and refresh the page.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, reactive, onMounted, watch, onUnmounted } from "vue";
  import { useStore } from "vuex";
  import {
    Camera,
    UploadCloud,
    Paperclip,
    Info,
    CheckCircle,
    XCircle,
    GraduationCap,
    Briefcase,
    ShieldCheck,
    AlertTriangle,
    Lock,
    FileText,
    Award,
    Eye,
    EyeOff,
  } from "lucide-vue-next";

  const store = useStore();

  // State
  const photoPreview = ref(null);
  const selectedPhotoFile = ref(null);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const loadingMessage = ref("Loading profile data...");

  const passwordForm = reactive({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // OTP state for first-time password setup
  const otp = ref("");
  const isOtpSending = ref(false);
  const otpSent = ref(false);
  const resendSeconds = ref(0);
  let resendTimerId = null;

  const form = reactive({
    email: "",
    phone: "",
  });
  const errors = reactive({
    email: "",
    phone: "",
  });
  const status = reactive({
    type: "", // 'success', 'error', 'info'
    message: "",
  });

  // Visible only for admin and when either field is missing
  const isVisible = computed(() => {
    const u = currentUser.value;
    return u?.role === "admin" && (!u?.email || !u?.phone);
  });

  const statusClass = computed(() => {
    if (status.type === "error") return "text-red-600";
    if (status.type === "success") return "text-green-700";
    return "text-blue-700";
  });

  // Simple validators
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9+()\-.\s]{7,20}$/;

  function validate() {
    errors.email = "";
    errors.phone = "";
    const u = currentUser.value;

    const wantsToSetEmail = !u?.email && form.email.length > 0;
    const wantsToSetPhone = !u?.phone && form.phone.length > 0;

    if (!wantsToSetEmail && !wantsToSetPhone) {
      status.type = "error";
      status.message = "Please provide at least one contact info to save.";
      return false;
    }

    if (wantsToSetEmail && !emailRegex.test(form.email)) {
      errors.email = "Please enter a valid email.";
      return false;
    }
    if (wantsToSetPhone && !phoneRegex.test(form.phone)) {
      errors.phone = "Please enter a valid phone number.";
      return false;
    }
    return true;
  }

  async function saveContact() {
    status.message = "";
    if (!validate()) return;

    const u = currentUser.value;
    const payload = {};

    if (!u?.email && form.email) payload.email = form.email.trim();
    if (!u?.phone && form.phone) payload.phone = form.phone.trim();

    if (Object.keys(payload).length === 0) {
      status.type = "info";
      status.message = "Nothing to update.";
      return;
    }

    isSaving.value = true;
    try {
      await store.dispatch("updateCurrentUserProfile", payload);
      await store.dispatch("fetchCurrentUser");
      status.type = "success";
      status.message = "Contact info updated successfully.";
      if (payload.email) form.email = "";
      if (payload.phone) form.phone = "";
    } catch (err) {
      const backendMessage = err?.response?.data?.message || err?.message;
      status.type = "error";
      status.message = backendMessage || "Failed to update contact info.";
    } finally {
      isSaving.value = false;
    }
  }

  // Password visibility states
  const currentPasswordType = ref("password");
  const newPasswordType = ref("password");
  const confirmPasswordType = ref("password");

  // Toast
  const toast = reactive({
    isVisible: false,
    message: "",
    type: "info",
  });
  let toastTimeoutId = null;

  // Computed properties from Vuex getters
  const currentUser = computed(() => store.state.currentUserDetails);

  // Detect if password is missing (initial setup)
  const isInitialPasswordSetup = computed(
    () => !store.state.currentUserDetails?.hasPassword,
  );

  // Local computed property for displaying photo URL
  const displayPhotoUrl = computed(() => {
    const path = currentUser.value?.photo;
    const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";
    if (!path) return null;
    return path.startsWith("http")
      ? path
      : `${baseURL.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
  });

  const toastClasses = computed(() => {
    const baseClasses = "transition-all duration-300 ease-in-out";
    switch (toast.type) {
      case "success":
        return `${baseClasses} bg-green-600`;
      case "error":
        return `${baseClasses} bg-red-600`;
      case "info":
      default:
        return `${baseClasses} bg-blue-600`;
    }
  });

  const canSendOtp = computed(() => !!currentUser.value?.phone);

  // Utils
  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString;
    }
  };
  const showToast = (msg, type = "info", duration = 3000) => {
    if (toastTimeoutId) clearTimeout(toastTimeoutId);
    toast.message = msg;
    toast.type = type;
    toast.isVisible = true;
    toastTimeoutId = setTimeout(() => {
      toast.isVisible = false;
    }, duration);
  };

  // Photo upload logic
  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      selectedPhotoFile.value = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        photoPreview.value = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      selectedPhotoFile.value = null;
      photoPreview.value = displayPhotoUrl.value;
    }
  };

  // Read file as Base64
  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });

  // Update profile photo
  const updateProfilePhoto = async () => {
    if (!selectedPhotoFile.value || !currentUser.value) {
      showToast("No new photo selected.", "info");
      return;
    }

    isSaving.value = true;
    try {
      const photoBase64 = await readFileAsBase64(selectedPhotoFile.value);
      await store.dispatch("updateCurrentUserProfile", { photoBase64 });
      await store.dispatch("fetchCurrentUser");
      photoPreview.value = null;
      selectedPhotoFile.value = null;
      showToast("Profile photo updated successfully!", "success");
    } catch (error) {
      console.error("Error updating profile photo:", error);
      showToast(error.message || "Failed to update profile photo.", "error");
    } finally {
      isSaving.value = false;
    }
  };

  // Strong password policy: 8+ chars with mixed classes
  const strongPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_+\-={}[\]|:;"'<>,./?]).{6,}$/;

  // Request OTP from backend and start resend cooldown
  const startResendTimer = (seconds = 60) => {
    resendSeconds.value = seconds;
    if (resendTimerId) clearInterval(resendTimerId);
    resendTimerId = setInterval(() => {
      if (resendSeconds.value <= 1) {
        clearInterval(resendTimerId);
        resendTimerId = null;
        resendSeconds.value = 0;
      } else {
        resendSeconds.value -= 1;
      }
    }, 1000);
  };

  const sendOtp = async () => {
    if (!canSendOtp.value) {
      showToast("Please add your phone number first.", "error");
      return;
    }
    if (resendSeconds.value > 0) return;

    isOtpSending.value = true;
    try {
      const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";
      const token = localStorage.getItem("token"); // get JWT

      const res = await fetch(
        `${baseURL.replace(/\/+$/, "")}/api/users/request-password-otp`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to send OTP");
      }

      const data = await res.json();
      otpSent.value = true;
      showToast(
        data.message || "OTP sent successfully. Please check your phone.",
        "success",
      );
      startResendTimer(60); // cooldown
    } catch (err) {
      showToast(
        err.message || "Unable to send OTP. Please try again.",
        "error",
      );
    } finally {
      isOtpSending.value = false;
    }
  };

  // Change/set password
  const changePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (isInitialPasswordSetup.value) {
      // First-time set: requires OTP + new + confirm
      if (!otp.value || otp.value.length !== 6) {
        showToast("Please enter the 6-digit OTP.", "error");
        return;
      }
      if (!newPassword || !confirmPassword) {
        showToast("New password and confirm password are required.", "error");
        return;
      }
      if (newPassword !== confirmPassword) {
        showToast("New password and confirm password do not match.", "error");
        return;
      }
      if (!strongPasswordRegex.test(newPassword)) {
        showToast(
          "Password must be 6+ characters and include uppercase, lowercase, number, and special character.",
          "error",
        );
        return;
      }

      isSaving.value = true;
      try {
        await store.dispatch("updateCurrentUserProfile", {
          newPassword,
          otp: otp.value,
        });
        showToast("Password set successfully!", "success");
        passwordForm.currentPassword = "";
        passwordForm.newPassword = "";
        passwordForm.confirmPassword = "";
        otp.value = "";
        await store.dispatch("fetchCurrentUser");
      } catch (error) {
        const backendMessage = error?.response?.data?.message;
        showToast(
          backendMessage || error?.message || "Failed to set password.",
          "error",
        );
        // Keep OTP so user can retry; reset new/confirm
        passwordForm.newPassword = "";
        passwordForm.confirmPassword = "";
      } finally {
        isSaving.value = false;
      }
    } else {
      // Regular change
      if (!currentPassword || !newPassword || !confirmPassword) {
        showToast("All fields are required.", "error");
        return;
      }
      if (currentPassword === newPassword) {
        showToast(
          "New password cannot be the same as the current password.",
          "error",
        );
        return;
      }
      if (newPassword !== confirmPassword) {
        showToast("New password and confirm password do not match.", "error");
        return;
      }
      if (!strongPasswordRegex.test(newPassword)) {
        showToast(
          "Password must be 8+ characters and include uppercase, lowercase, number, and special character.",
          "error",
        );
        return;
      }

      isLoading.value = true;
      try {
        await store.dispatch("updateCurrentUserProfile", {
          currentPassword,
          newPassword,
        });
        showToast("Password changed successfully!", "success");
        passwordForm.currentPassword = "";
        passwordForm.newPassword = "";
        passwordForm.confirmPassword = "";
      } catch (error) {
        const backendMessage = error?.response?.data?.message;
        showToast(
          backendMessage ||
            error?.message ||
            "Failed to change password. Please check your current password.",
          "error",
        );
        // Keep current so user can retry, reset new/confirm
        passwordForm.newPassword = "";
        passwordForm.confirmPassword = "";
      } finally {
        isLoading.value = false;
      }
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    if (field === "current") {
      currentPasswordType.value =
        currentPasswordType.value === "password" ? "text" : "password";
    } else if (field === "new") {
      newPasswordType.value =
        newPasswordType.value === "password" ? "text" : "password";
    } else if (field === "confirm") {
      confirmPasswordType.value =
        confirmPasswordType.value === "password" ? "text" : "password";
    }
  };

  // Fetch user data on component mount
  onMounted(async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchCurrentUser");
    } catch (error) {
      console.error("Error fetching current user:", error);
      showToast("Failed to load profile data.", "error");
    } finally {
      isLoading.value = false;
    }
  });

  // Keep preview in sync
  watch(
    currentUser,
    (/* newVal */) => {
      if (!photoPreview.value || photoPreview.value === displayPhotoUrl.value) {
        photoPreview.value = displayPhotoUrl.value || null;
      }
    },
    { immediate: true, deep: true },
  );

  // Reset forms on user update
  watch(
    currentUser,
    (u) => {
      if (!u) return;
      form.email = u.email ? "" : "";
      form.phone = u.phone ? "" : "";
      status.message = "";
      errors.email = "";
      errors.phone = "";
    },
    { immediate: true },
  );

  onUnmounted(() => {
    if (toastTimeoutId) clearTimeout(toastTimeoutId);
    if (resendTimerId) clearInterval(resendTimerId);
  });
</script>

<style scoped>
  /* Basic spinner animation */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }

  /* Toast transition styles */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(20px);
  }

  /* Disabled state improvements */
  .disabled\:opacity-50:disabled {
    opacity: 0.5;
  }
  .disabled\:cursor-not-allowed:disabled {
    cursor: not-allowed;
  }
</style>
